import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosServices from '../../utils/axios';
import { setSession } from '../../guards/jwt/Jwt';
import { setSentryUser, captureAppException, SENTRY_SECTION, SENTRY_ACTION } from '../../utils/sentry';
import { captureApiError } from '../../utils/apiErrorHandler';

// Define the initial state
const initialState = {
  isAuthenticated: false,
  userProfile: {},
  user: null,
  loading: false,
  error: null,
  showLoginNotices: false, // Login sonrası duyuru gösterimi için
  passwordReset: {
    loading: false,
    error: null,
    success: false
  },
  passwordChange: {
    loading: false,
    error: null,
    success: false
  },
  performance: {
    mtTotalNotifications: 0,
    mtTotalServices: 0,
    mtServicesAmount: 0,
    dbm: 0,
    mtSurvey: 0,
    mtAnswer: 0, 
    loading: false,
    error: null
  },
  supplier: {
    loading: false,
    error: null,
    smsStatus: null,
    token: null
  }
};

// Create async thunk for login
export const loginUser = createAsyncThunk(
  'userProfile/loginUser',
  async ({ dsUserName, dsPassword }, { dispatch, rejectWithValue }) => {
    try {
      const loginResponse = await axiosServices.post(
        `${import.meta.env.VITE_REACT_APP_APIURL}/user-service/api/Account/login-for-callcenter`,
        { dsUserName, dsPassword }
      );

      // Check the response structure
      if (!loginResponse.data?.data) {
        throw new Error('Geçersiz giriş yanıtı');
      }

      const { data, statu, exceptions } = loginResponse.data;

      // Handle successful login
      if (data.flLogin === true) {
        const { data } = loginResponse.data.data;
        // 2FA kontrolü 
        return {
          requires2FA: true,
          userData: {
            ...loginResponse.data.data
          }
        };
      } else {
        throw new Error(exceptions.length > 0 ? exceptions[0].Message : 'Giriş başarısız');
      }
    } catch (error) {
      captureApiError(error, {
        endpoint: `${import.meta.env.VITE_REACT_APP_APIURL}/user-service/api/Account/login-for-callcenter`,
        method: 'POST',
        status: error.response?.status,
        section: SENTRY_SECTION.auth,
        action: 'login_user',
      });
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.exceptions?.[0]?.Message ||
        'Giriş başarısız'
      );
    }
  }
);

export const verify2FA = createAsyncThunk(
  'userProfile/verify2FA',
  async ({ key, code, dsUserName, dsPassword }, { rejectWithValue }) => {
    try {
      const response = await axiosServices.post(
        `${import.meta.env.VITE_REACT_APP_APIURL}/user-service/api/Account/check-google-callcenter`,
        { key, code, dsUserName, dsPassword }
      );

      if (response.data?.data) {
        const { dsToken, dsMail, dsNameSurname, idCcnUser } = response.data.data;
        setSession(dsToken, dsMail, dsNameSurname, idCcnUser);
        setSentryUser({
          id: String(idCcnUser),
          email: dsMail || undefined,
          username: dsNameSurname || undefined,
        });

        return {
          userProfile: response.data.data
        };
      }

      throw new Error('Doğrulama başarısız');
    } catch (error) {
      captureAppException(error, {
        tags: {
          section: SENTRY_SECTION.auth,
          action: SENTRY_ACTION.auth_verify_2fa,
        },
      });
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.exceptions?.[0]?.Message ||
        'Doğrulama başarısız'
      );
    }
  }
);

// Şifre sıfırlama için SMS gönderimi
export const sendResetPasswordSMS = createAsyncThunk(
  'userProfile/sendResetPasswordSMS',
  async ({ dsPhone }, { rejectWithValue }) => {
    try {
      const response = await axiosServices.post(
        `${import.meta.env.VITE_REACT_APP_APIURL}/user-service/api/User/change-password-dealer?dsPhone=${dsPhone}`,
        {
          headers: {
            'accept': 'text/plain'
          }
        }
      );

      if (!response.data?.data) {
        throw new Error('SMS gönderilemedi');
      }

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.exceptions?.[0]?.Message ||
        'SMS gönderimi başarısız oldu'
      );
    }
  }
);

// SMS kodu doğrulama ve yeni şifre belirleme
export const verifyResetPasswordSMS = createAsyncThunk(
  'userProfile/verifyResetPasswordSMS',
  async ({ dsPhone, smsCode, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axiosServices.post(
        `${import.meta.env.VITE_REACT_APP_APIURL}/user-service/api/User/check-sms-code?dsPhone=${dsPhone}&smsCode=${smsCode}&newPassword=${newPassword}`
      );

      if (!response.data?.data) {
        throw new Error('Şifre değiştirilemedi');
      }

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.exceptions?.[0]?.Message ||
        'Şifre değiştirme işlemi başarısız oldu. Bilgilerinizi kontrol ediniz.'
      );
    }
  }
);

// QR kod alma action'ı
export const getGoogleQR = createAsyncThunk(
  'userProfile/getGoogleQR',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosServices.get(
        `${import.meta.env.VITE_REACT_APP_APIURL}/dealer-service/api/App/get-google-qr`
      );

      if (!response.data?.data) {
        throw new Error('QR kod alınamadı');
      }

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.exceptions?.[0]?.Message ||
        'QR kod alınamadı'
      );
    }
  }
);

// 2FA durumunu güncelleme action'ı
export const updateGoogleAuth = createAsyncThunk(
  'userProfile/updateGoogleAuth',
  async ({ idDealer, flGoogleAuth }, { rejectWithValue }) => {
    try {
      const response = await axiosServices.post(
        `${import.meta.env.VITE_REACT_APP_APIURL}/dealer-service/api/Account/update-google-qr?idDealer=${idDealer}&flGoogleAuth=${flGoogleAuth}`,
      );

      if (!response.data?.data) {
        throw new Error('Güncelleme başarısız');
      }

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.exceptions?.[0]?.Message ||
        'Güncelleme başarısız'
      );
    }
  }
);

// Performans verilerini getirmek için async thunk
export const getUserPerformance = createAsyncThunk(
  'userProfile/getUserPerformance',
  async ({ year, month }, { rejectWithValue }) => {
    try {
      const response = await axiosServices.get(
        `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/App/get-users-performance?year=${year}&month=${month}`,
      );

      if (!response.data?.data) {
        throw new Error('Performans verileri alınamadı');
      }

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.exceptions?.[0]?.Message ||
        'Performans verileri alınamadı'
      );
    }
  }
);

// Create async thunk for logout
export const logoutUser = createAsyncThunk(
  'user/logout',
  async () => {
    localStorage.removeItem('token');
    return null;
  }
);

// SMS gönderimi için async thunk
export const sendSupplierLoginSMS = createAsyncThunk(
    'userProfile/sendSupplierLoginSMS',
    async ({ dsPhone }, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/user-service/api/Account/get-sms-for-login?dsPhone=${dsPhone}`,
            );

            if (response.data?.statu !== 200) {
                throw new Error(response.data?.exceptions?.[0] || 'SMS gönderilemedi');
            }

            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.exceptions?.[0] || 
                'SMS gönderimi başarısız oldu'
            );
        }
    }
);

// Tedarikçi girişi için async thunk
export const loginSupplier = createAsyncThunk(
    'userProfile/loginSupplier',
    async ({ dsUserName, dsPassword }, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/user-service/api/Account/login-for-suppplier`,
                {
                    "dsUserName": dsUserName,
                    "dsPassword": dsPassword
                }
            );

            // API yanıtını kontrol et
            if (!response.data) {
                throw new Error('API yanıtı alınamadı');
            }

            const { data, statu, exceptions } = response.data;

            if (statu === 200 && data) {
                return {
                    token: data,
                    success: true
                };
            }

            throw new Error(exceptions?.[0] || 'Giriş başarısız');
        } catch (error) {
            console.error('Login Error:', error);
            return rejectWithValue(
                error.response?.data?.exceptions?.[0] || 
                error.message ||
                'Giriş işlemi başarısız oldu'
            );
        }
    }
);

// Parola değiştirme için async thunk
export const changePassword = createAsyncThunk(
    'userProfile/changePassword',
    async ({ idUser, dsPassword, dsNewPassword, dsNewPasswordAgain }, { rejectWithValue }) => {
        try {
            const response = await axiosServices.post(
                `${import.meta.env.VITE_REACT_APP_APIURL}/callcenter-service/api/App/password-change`,
                {
                    idUser,
                    dsPassword,
                    dsNewPassword,
                    dsNewPasswordAgain
                }
            );

            if (response.data?.statu === 200) {
                return response.data.data;
            }

            // API'den hata response'u geldi (statu !== 200)
            if (response.data?.statu !== 200 && response.data?.exceptions && response.data.exceptions.length > 0) {
                return rejectWithValue(response.data);
            }

            return rejectWithValue({
                statu: response.data?.statu || 500,
                exceptions: response.data?.exceptions || [{ message: 'Parola değiştirilemedi' }]
            });
        } catch (error) {
            // Axios interceptor'ı error.response.data'yı döndürüyor
            if (error?.statu && error?.exceptions) {
                return rejectWithValue(error);
            }
            
            if (error?.response?.data) {
                return rejectWithValue(error.response.data);
            }
            
            return rejectWithValue({ 
                statu: 500,
                exceptions: [{ message: error?.message || 'Parola değiştirilemedi' }]
            });
        }
    }
);



// Create the user slice
const userSlice = createSlice({
  name: 'UserDetail',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSupplierError: (state) => {
      state.supplier.error = null;
    },
    clearSupplierData: (state) => {
      state.supplier = initialState.supplier;
    },
    setShowLoginNotices: (state, action) => {
      state.showLoginNotices = action.payload;
    },
    clearLoginNotices: (state) => {
      state.showLoginNotices = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      //Verify 2FA cases
      .addCase(verify2FA.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verify2FA.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.userProfile = action.payload.userProfile;
        state.showLoginNotices = true; // Login başarılı sonrası duyuru göster
        state.error = null;
      })
      .addCase(verify2FA.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Şifre sıfırlama SMS cases
      .addCase(sendResetPasswordSMS.pending, (state) => {
        state.passwordReset.loading = true;
        state.passwordReset.error = null;
        state.passwordReset.success = false;
      })
      .addCase(sendResetPasswordSMS.fulfilled, (state) => {
        state.passwordReset.loading = false;
        state.passwordReset.success = true;
      })
      .addCase(sendResetPasswordSMS.rejected, (state, action) => {
        state.passwordReset.loading = false;
        state.passwordReset.error = action.payload;
      })

      // SMS doğrulama cases
      .addCase(verifyResetPasswordSMS.pending, (state) => {
        state.passwordReset.loading = true;
        state.passwordReset.error = null;
      })
      .addCase(verifyResetPasswordSMS.fulfilled, (state) => {
        state.passwordReset.loading = false;
        state.passwordReset.success = true;
      })
      .addCase(verifyResetPasswordSMS.rejected, (state, action) => {
        state.passwordReset.loading = false;
        state.passwordReset.error = action.payload;
      })

      // QR kod alma cases
      .addCase(getGoogleQR.pending, (state) => {
        state.loading = 'loading';
        state.error = null;
      })
      .addCase(getGoogleQR.fulfilled, (state) => {
        state.loading = 'idle';
      })
      .addCase(getGoogleQR.rejected, (state, action) => {
        state.loading = 'idle';
        state.error = action.payload;
      })

      // 2FA durumunu güncelleme cases
      .addCase(updateGoogleAuth.pending, (state) => {
        state.loading = 'loading';
        state.error = null;
      })
      .addCase(updateGoogleAuth.fulfilled, (state) => {
        state.loading = 'idle';
      })
      .addCase(updateGoogleAuth.rejected, (state, action) => {
        state.loading = 'idle';
        state.error = action.payload;
      })

      // Performans cases
      .addCase(getUserPerformance.pending, (state) => {
        state.performance.loading = true;
        state.performance.error = null;
      })
      .addCase(getUserPerformance.fulfilled, (state, action) => {
        state.performance.loading = false;
        const userPerformance = action.payload.find(item => item.idCcnUser === parseInt(localStorage.getItem('idCcnUser')));
        state.performance.mtTotalNotifications = userPerformance?.mtTotalNotifications || 0;
        state.performance.mtTotalServices = userPerformance?.mtTotalServices || 0;
        state.performance.mtServicesAmount = userPerformance?.mtServicesAmount || 0;
        state.performance.dbm = userPerformance?.dbm || 0;
        state.performance.mtSurvey = userPerformance?.mtSurvey || 0;
        state.performance.mtAnswer = userPerformance?.mtAnswer || 0;
        state.performance.error = null;
      })
      .addCase(getUserPerformance.rejected, (state, action) => {
        state.performance.loading = false;
        state.performance.error = action.payload;
      })

      // Logout cases
      .addCase(logoutUser.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
      })

      // Supplier SMS cases
      .addCase(sendSupplierLoginSMS.pending, (state) => {
        state.supplier.loading = true;
        state.supplier.error = null;
      })
      .addCase(sendSupplierLoginSMS.fulfilled, (state, action) => {
        state.supplier.loading = false;
        state.supplier.smsStatus = action.payload;
      })
      .addCase(sendSupplierLoginSMS.rejected, (state, action) => {
        state.supplier.loading = false;
        state.supplier.error = action.payload;
      })

      // Supplier Login cases
      .addCase(loginSupplier.pending, (state) => {
        state.supplier.loading = true;
        state.supplier.error = null;
      })
      .addCase(loginSupplier.fulfilled, (state, action) => {
        state.supplier.loading = false;
        state.supplier.token = action.payload.token;
      })
      .addCase(loginSupplier.rejected, (state, action) => {
        state.supplier.loading = false;
        state.supplier.error = action.payload;
      })

      // Password Change cases
      .addCase(changePassword.pending, (state) => {
        state.passwordChange.loading = true;
        state.passwordChange.error = null;
        state.passwordChange.success = false;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.passwordChange.loading = false;
        state.passwordChange.success = true;
        state.passwordChange.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.passwordChange.loading = false;
        state.passwordChange.success = false;
        state.passwordChange.error = action.payload;
      });
  },
});

export const { clearError, clearSupplierError, clearSupplierData, setShowLoginNotices, clearLoginNotices } = userSlice.actions;

export default userSlice.reducer;